import { formatCents, formatPercent, tt } from '@agos/shared'

/**
 * What the three dashboard endpoints answer, and what the legacy screen did with it.
 *
 * **FB-172.** Four of the five roles are shown *Prehľad* and served nothing. The sidebar gates it on the
 * permission `dashboard`, which `role_permissions` grants to **every** role — `main_admin`, `admin`, `cook`,
 * `waiter`, `driver` — while all three endpoints are `@RequirePermission({"statistics", "statistics_view"})`
 * and only `main_admin` holds either. `RequirePermission` lets `admin` through unconditionally, so the split
 * measured against the live backend is:
 *
 * | Role | `/dashboard/stats`, `/revenue-chart`, `/daily` |
 * |---|---|
 * | `main_admin`, `admin` | 200 |
 * | `cook`, `waiter`, `driver` | **403 on all three** |
 *
 * Legacy's queries throw on `!r.ok`, and the page renders `statsLoading ? skeleton : stats ? cards : null`.
 * So a cook logging in lands on `/` — the app's first screen — and gets a filter bar, a chart heading, an
 * empty chart and nothing else. No error, no explanation, no indication that anything was refused.
 *
 * **FB-173.** Three figures are fetched and thrown away. `deliveryCost` (1 821,90 € in June) is in the
 * `Stats` interface and on no line of the render. `deliveryOrders` and `restaurantOrders` are in the `DayRow`
 * interface and in no column — and the payload also carries `barOrders`, which the interface does not even
 * declare. So the per-day channel split is requested every time the table loads and shown never.
 *
 * **FB-174.** The third channel is called two different things. `sales_channels` names it **BAR**, and the
 * §10A `revenueByChannel` block returns that name with its own order count; legacy hard-codes the label
 * *Osobný odber* — personal pickup — for the same money in five places. A bar tab and a customer collecting
 * an order are not the same trade. The server's name is used, and the channel list comes from the payload
 * rather than from three hard-coded cards, so a franchise that switches on the fourth channel
 * (`na_sekeru`, inactive here) sees it.
 *
 * Money is integer cents per §14.7. The API sends decimal euros, so it is converted once on the way in.
 */

function euros(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.round(value * 100)
  if (typeof value === 'string' && value !== '') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? Math.round(parsed * 100) : 0
  }
  return 0
}

function count(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.round(value) : 0
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null
}

// ─── The stats block ─────────────────────────────────────────────────────────

export interface ChannelRow {
  slug: string
  name: string
  revenueCents: number
  orders: number
}

export interface Stats {
  totalRevenueCents: number
  deliveryRevenueCents: number
  restaurantRevenueCents: number
  barRevenueCents: number
  totalOrders: number
  cancelledOrders: number
  averageOrderValueCents: number
  deliveryCostCents: number
  /**
   * What delivery **cost** - payouts, purchases and fuel from the handovers, §10A.187.
   *
   * Beside `deliveryCostCents`, which is the fee **charged** (`SUM(orders.delivery_cost)`) and was
   * mislabelled as a cost until §9.411. The two move on different clocks: the fee with every order,
   * this one when a driver settles up - which is why §9.412's tile states its period.
   *
   * Absent on a payload from a server that predates §10A.187, and 0 is the safe reading: a tile that
   * invented a cost would be worse than one that has nothing to show yet.
   */
  deliveryExpenseCents: number
  /**
   * How many handovers the period holds - §10A.188, §9.413.
   *
   * **Why a count rides along with the sum.** The owner read the tile's 0,00 as wrong and it was
   * right, answering the wrong question: their figures were on the *odovzdať tržbu* form,
   * unsubmitted, so there was no row. `COALESCE(SUM(...), 0)` cannot tell *nobody handed over* from
   * *a shift that spent nothing*, and a nullable sum would not have survived either - `euros(null)`
   * is 0 here, so the distinction would be lost at exactly the boundary that has to keep it.
   *
   * 0 on a payload from a server that predates §10A.188, which reads as *no handovers* and makes
   * the tile say so rather than claim a measured nought.
   */
  deliveryExpenseHandovers: number
  discountsCents: number
  cashPaymentsCents: number
  cardPaymentsCents: number
  courierPaymentsCents: number
  onlinePaymentsCents: number
  /** §10A: the channels by name, with order counts. Legacy has no idea this exists. */
  channels: ChannelRow[]
}

export function statsOf(payload: unknown): Stats | null {
  if (payload === null || payload === undefined || typeof payload !== 'object') return null
  const row = payload as Record<string, unknown>
  const channels = Array.isArray(row.revenueByChannel) ? row.revenueByChannel : []
  return {
    totalRevenueCents: euros(row.totalRevenue),
    deliveryRevenueCents: euros(row.deliveryRevenue),
    restaurantRevenueCents: euros(row.restaurantRevenue),
    barRevenueCents: euros(row.barRevenue),
    totalOrders: count(row.totalOrders),
    cancelledOrders: count(row.cancelledOrders),
    averageOrderValueCents: euros(row.averageOrderValue),
    deliveryCostCents: euros(row.deliveryCost),
    deliveryExpenseCents: euros(row.deliveryExpense),
    deliveryExpenseHandovers: count(row.deliveryExpenseHandovers),
    discountsCents: euros(row.discounts),
    cashPaymentsCents: euros(row.cashPayments),
    cardPaymentsCents: euros(row.cardPayments),
    courierPaymentsCents: euros(row.courierPayments),
    onlinePaymentsCents: euros(row.onlinePayments),
    channels: channels.map((entry) => {
      const channel = entry as Record<string, unknown>
      const slug = text(channel.slug) ?? ''
      return {
        slug,
        // The server's own name, not a label this screen invents. FB-174.
        name: text(channel.name) ?? slug,
        revenueCents: euros(channel.revenue),
        orders: count(channel.orders),
      }
    }),
  }
}

/**
 * The channels to draw.
 *
 * `revenueByChannel` when the server sent it; otherwise the three flat fields, so an older backend still
 * renders. A franchise with a fourth channel gets a fourth row either way round.
 */
export function channelsOf(stats: Stats): ChannelRow[] {
  if (stats.channels.length > 0) return stats.channels
  return [
    { slug: 'delivery', name: tt('Rozvoz'), revenueCents: stats.deliveryRevenueCents, orders: 0 },
    { slug: 'restaurant', name: tt('Reštaurácia'), revenueCents: stats.restaurantRevenueCents, orders: 0 },
    { slug: 'bar', name: tt('BAR'), revenueCents: stats.barRevenueCents, orders: 0 },
  ]
}

export interface PaymentRow {
  key: string
  label: string
  cents: number
}

export function paymentsOf(stats: Stats): PaymentRow[] {
  return [
    { key: 'cash', label: tt('Hotovosť'), cents: stats.cashPaymentsCents },
    { key: 'card', label: tt('Platobná karta (terminál)'), cents: stats.cardPaymentsCents },
    { key: 'courier', label: tt('Karta kuriér'), cents: stats.courierPaymentsCents },
    { key: 'online', label: tt('CardPay Online'), cents: stats.onlinePaymentsCents },
  ]
}

/**
 * A part's share of the takings, or `null` when there are none.
 *
 * Legacy divides by `totalRevenue || 1`, so a day that has taken nothing reports every share as **0 %** —
 * a measurement of a day that has not happened yet. On the *Dnes* preset before opening, that is the whole
 * screen. `null` reads as *nemerané* and cannot be mistaken for a slow morning.
 */
export function shareOf(partCents: number, totalCents: number): number | null {
  if (totalCents <= 0) return null
  return (partCents / totalCents) * 100
}

export function shareLabel(partCents: number, totalCents: number): string {
  const share = shareOf(partCents, totalCents)
  return share === null ? tt('nemerané') : formatPercent(share, 1)
}

/**
 * The discount share, against what was billed rather than what was banked.
 *
 * `discounts / (revenue + discounts)`: the denominator is the till before the discount came off, which is
 * what "x % of takings was given away" means. Legacy has this one right and it is kept.
 */
export function discountShare(stats: Stats): number | null {
  const billed = stats.totalRevenueCents + stats.discountsCents
  if (billed <= 0) return null
  return (stats.discountsCents / billed) * 100
}

/**
 * Whether the parts add up to the whole.
 *
 * Both breakdowns are exhaustive on this data — 41 815,85 + 7 876,20 + 4 867,50 = 54 559,55 and the four
 * payment methods total the same — so the shares sum to 100 %. Worth checking rather than assuming: a
 * channel added server-side and left out of one of the two sums would make a bar chart quietly lie, and
 * nothing else on the page would show it.
 */
export interface Reconciliation {
  channelsCents: number
  paymentsCents: number
  channelsMatch: boolean
  paymentsMatch: boolean
}

export function reconcile(stats: Stats): Reconciliation {
  // A plain integer reduce: `sumCents` parses its inputs, so feeding it cents multiplies by a hundred.
  const channelsCents = channelsOf(stats).reduce((total, channel) => total + channel.revenueCents, 0)
  const paymentsCents = paymentsOf(stats).reduce((total, payment) => total + payment.cents, 0)
  return {
    channelsCents,
    paymentsCents,
    channelsMatch: channelsCents === stats.totalRevenueCents,
    paymentsMatch: paymentsCents === stats.totalRevenueCents,
  }
}

export function reconciliationNote(stats: Stats): string {
  const check = reconcile(stats)
  if (check.channelsMatch && check.paymentsMatch) return ''
  const parts: string[] = []
  if (!check.channelsMatch) {
    parts.push(`${tt('kanály')} ${formatCents(check.channelsCents)}`)
  }
  if (!check.paymentsMatch) {
    parts.push(`${tt('platby')} ${formatCents(check.paymentsCents)}`)
  }
  return `${tt('Rozpad nesedí s celkovou tržbou')} ${formatCents(stats.totalRevenueCents)}: ${parts.join(', ')}. ${tt('Percentá nižšie preto nedávajú sto.')}`
}

// ─── The chart ───────────────────────────────────────────────────────────────

export interface ChartPoint {
  bucket: string
  deliveryCents: number
  restaurantCents: number
  barCents: number
}

/**
 * The chart buckets.
 *
 * The field is called `hour` in both shapes — `"14:00"` for a single day, `"06-01"` for a range — because
 * the endpoint reuses the key. Renamed to `bucket` here so a reader of this module is not told it is an hour
 * when it is a date.
 */
export function chartOf(payload: unknown): ChartPoint[] {
  if (!Array.isArray(payload)) return []
  return payload.map((entry) => {
    const point = entry as Record<string, unknown>
    return {
      bucket: text(point.hour) ?? '',
      deliveryCents: euros(point.delivery),
      restaurantCents: euros(point.restaurant),
      barCents: euros(point.bar),
    }
  })
}

export function chartTotalCents(points: readonly ChartPoint[]): number {
  return points.reduce(
    (total, point) => total + point.deliveryCents + point.restaurantCents + point.barCents,
    0,
  )
}

/** A chart with nothing in it, as opposed to a chart that has not loaded. */
export function chartIsEmpty(points: readonly ChartPoint[]): boolean {
  return points.length === 0 || chartTotalCents(points) === 0
}

// ─── The per-day table ───────────────────────────────────────────────────────

export interface DayRow {
  date: string
  totalRevenueCents: number
  totalOrders: number
  averageOrderValueCents: number
  discountsCents: number
  cancelledOrders: number
  deliveryOrders: number
  restaurantOrders: number
  barOrders: number
}

export function dailyOf(payload: unknown): DayRow[] {
  if (!Array.isArray(payload)) return []
  return payload.flatMap((entry) => {
    const row = entry as Record<string, unknown>
    const date = text(row.date)
    if (date === null) return []
    return [
      {
        date,
        totalRevenueCents: euros(row.totalRevenue),
        totalOrders: count(row.totalOrders),
        averageOrderValueCents: euros(row.averageOrderValue),
        discountsCents: euros(row.discounts),
        cancelledOrders: count(row.cancelledOrders),
        // FB-173: all three are in the payload and on no legacy column.
        deliveryOrders: count(row.deliveryOrders),
        restaurantOrders: count(row.restaurantOrders),
        barOrders: count(row.barOrders),
      },
    ]
  })
}

/**
 * The rows oldest-first.
 *
 * The endpoint answers newest-first and legacy renders it in the order received, so on one screen the chart
 * runs left-to-right through the month while the table under it runs top-to-bottom backwards. Sorting here
 * makes the two agree; the day marker still finds today wherever it is.
 */
export function dailyAscending(rows: readonly DayRow[]): DayRow[] {
  return [...rows].sort((left, right) => left.date.localeCompare(right.date))
}

export function busiestDayCents(rows: readonly DayRow[]): number {
  return rows.reduce((most, row) => Math.max(most, row.totalRevenueCents), 0)
}

/** The bar width for one day, as a share of the busiest. Zero when nothing was taken all range. */
export function dayBarPercent(row: DayRow, rows: readonly DayRow[]): number {
  const busiest = busiestDayCents(rows)
  if (busiest <= 0) return 0
  return (row.totalRevenueCents / busiest) * 100
}

/**
 * Whether the per-day rows add up to the range total the KPI cards show.
 *
 * They do on this data — 30 rows summing to 54 559,55 € and 2 638 orders, exactly the stats block — which is
 * why the *SPOLU* row can print the stats figures beside a column of days. If they ever part company, the
 * footer would be quietly claiming a sum of the rows above it that is not their sum.
 */
export function dailyMatchesStats(rows: readonly DayRow[], stats: Stats | null): boolean {
  if (stats === null || rows.length === 0) return true
  const revenue = rows.reduce((total, row) => total + row.totalRevenueCents, 0)
  const orders = rows.reduce((total, row) => total + row.totalOrders, 0)
  return revenue === stats.totalRevenueCents && orders === stats.totalOrders
}

export function dailyMismatchNote(rows: readonly DayRow[], stats: Stats | null): string {
  if (dailyMatchesStats(rows, stats) || stats === null) return ''
  const revenue = rows.reduce((total, row) => total + row.totalRevenueCents, 0)
  return `${tt('Súčet dní')} ${formatCents(revenue)} ${tt('sa nerovná tržbe za obdobie')} ${formatCents(stats.totalRevenueCents)}. ${tt('Riadok SPOLU ukazuje tržbu za obdobie.')}`
}

// ─── What the page may show at all ───────────────────────────────────────────

/**
 * FB-172. The permission the endpoints want, which is not the one that put the item in the sidebar.
 *
 * Stated rather than guessed: `@RequirePermission({"statistics", "statistics_view"})` on all three, and
 * `RequirePermission` admits `main_admin` and `admin` whatever they hold.
 */
export const DASHBOARD_DATA_PERMISSIONS: readonly string[] = ['statistics', 'statistics_view']

export function mayReadDashboard(
  role: string | undefined,
  hasPermission: (permission: string) => boolean,
): boolean {
  if (role === 'main_admin' || role === 'admin') return true
  return DASHBOARD_DATA_PERMISSIONS.some((permission) => hasPermission(permission))
}

export function refusalNote(role: string | undefined): string {
  if (role === undefined) {
    return tt('Na čísla z tejto obrazovky nemáte právo.')
  }
  return tt(
    'Túto obrazovku máte v menu, ale čísla na nej vám server nevydá - potrebujete právo „statistics“. Do vtedy tu nebude nič, a nie preto, že sa nič nepredalo.',
  )
}

/**
 * What the delivery-cost tile shows - §9.413, §10A.188.
 *
 * **Here rather than inline in the JSX so it can be proved by reverting.** A ternary inside a
 * `<StatCard>` is reachable only by rendering the whole dashboard, which needs six query hooks; the
 * decision it encodes is worth a test on its own.
 *
 * **The decision: a dash when nobody has handed over, a figure when somebody has.** The owner read
 * the tile's 0,00 as wrong - *"shows 0,00 even tho they already have them"* - and it was right and
 * answering the wrong question. Their 47,50 and 4,15 were on the *odovzdať tržbu* form, unsubmitted,
 * so there was no row: `COALESCE(SUM(...), 0)` gives 0 for *nobody handed over yet* exactly as it
 * gives 0 for *a shift that spent nothing*. A dash reads as nothing to show; a figure reads as a
 * measurement.
 *
 * **And a shift that genuinely spent nothing still shows 0,00**, which is the case that keeps this
 * honest: the dash is about the absence of a handover, never about the absence of a cost.
 */
export function deliveryExpenseTile(stats: {
  deliveryExpenseCents: number
  deliveryExpenseHandovers: number
}): { value: string; sub: string } {
  if (stats.deliveryExpenseHandovers === 0) {
    return { value: '-', sub: tt('žiadna odovzdaná zmena v tomto období') }
  }
  return {
    value: formatCents(stats.deliveryExpenseCents),
    sub: `${tt('výplaty, nákupy a palivo')} - ${stats.deliveryExpenseHandovers} ${tt('odovzdaných zmien')}`,
  }
}
